import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Sparkles, Download, Share2, Smartphone, Zap, TrendingUp } from 'lucide-react';

export default function MemeMakerLanding() {
  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-900 via-purple-800 to-indigo-900">
      {/* Header */}
      <header className="border-b border-white/10 bg-black/20 backdrop-blur-sm">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Sparkles className="h-8 w-8 text-yellow-400" />
              <h1 className="text-2xl font-bold text-white">MimeMaker</h1>
            </div>
            <Link to="/meme-maker">
              <Button className="bg-yellow-400 text-purple-900 hover:bg-yellow-300 font-bold">
                Start Creating →
              </Button>
            </Link>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="container mx-auto px-4 py-16 md:py-24">
        <div className="text-center max-w-4xl mx-auto">
          <div className="inline-block mb-4 px-4 py-2 bg-yellow-400/20 border border-yellow-400/30 rounded-full">
            <span className="text-yellow-400 text-sm font-semibold">✨ FREE Meme Generator</span>
          </div>
          
          <h1 className="text-4xl md:text-6xl font-bold text-white mb-6">
            Create Viral Memes in
            <span className="block text-yellow-400 mt-2">Seconds</span>
          </h1>
          
          <p className="text-xl text-gray-300 mb-8 max-w-2xl mx-auto">
            Upload your photo or choose from 50+ popular templates. Add text, customize, and share your memes instantly!
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <Link to="/meme-maker">
              <Button size="lg" className="bg-yellow-400 text-purple-900 hover:bg-yellow-300 font-bold text-lg px-8 py-6">
                <Sparkles className="mr-2 h-5 w-5" />
                Start Creating Free
              </Button>
            </Link>
            <Button size="lg" variant="outline" className="border-white/30 text-white hover:bg-white/10">
              See Templates
            </Button>
          </div>
          
          <div className="mt-8 flex items-center justify-center gap-6 text-gray-400 text-sm">
            <div className="flex items-center gap-2">
              <TrendingUp className="h-4 w-4 text-green-400" />
              <span>1M+ memes created</span>
            </div>
            <div className="flex items-center gap-2">
              <Zap className="h-4 w-4 text-yellow-400" />
              <span>No signup required</span>
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="container mx-auto px-4 py-16">
        <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
          <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-2xl p-6 text-center">
            <div className="inline-flex items-center justify-center w-12 h-12 bg-yellow-400/20 rounded-full mb-4">
              <Smartphone className="h-6 w-6 text-yellow-400" />
            </div>
            <h3 className="text-xl font-bold text-white mb-2">Mobile Optimized</h3>
            <p className="text-gray-300">Works perfectly on phone, tablet, and desktop. Create memes anywhere!</p>
          </div>
          
          <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-2xl p-6 text-center">
            <div className="inline-flex items-center justify-center w-12 h-12 bg-yellow-400/20 rounded-full mb-4">
              <Download className="h-6 w-6 text-yellow-400" />
            </div>
            <h3 className="text-xl font-bold text-white mb-2">HD Downloads</h3>
            <p className="text-gray-300">Download your memes in high quality. No watermarks on premium version!</p>
          </div>
          
          <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-2xl p-6 text-center">
            <div className="inline-flex items-center justify-center w-12 h-12 bg-yellow-400/20 rounded-full mb-4">
              <Share2 className="h-6 w-6 text-yellow-400" />
            </div>
            <h3 className="text-xl font-bold text-white mb-2">Instant Share</h3>
            <p className="text-gray-300">Share directly to Instagram, TikTok, Twitter with one click!</p>
          </div>
        </div>
      </section>

      {/* Popular Templates Preview */}
      <section className="container mx-auto px-4 py-16">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">50+ Popular Templates</h2>
          <p className="text-gray-300 text-lg">Choose from trending meme formats or upload your own image</p>
        </div>
        
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-5xl mx-auto">
          {['Drake Hotline', 'Distracted Boyfriend', 'Woman Yelling at Cat', 'Two Buttons', 'Change My Mind', 'Bernie Sanders', 'Galaxy Brain', 'Expanding Brain'].map((template, idx) => (
            <div key={idx} className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-lg p-4 hover:bg-white/20 transition-all cursor-pointer">
              <div className="aspect-square bg-gradient-to-br from-purple-600 to-indigo-600 rounded-lg mb-2 flex items-center justify-center">
                <Sparkles className="h-8 w-8 text-white/50" />
              </div>
              <p className="text-white text-sm font-medium text-center">{template}</p>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="container mx-auto px-4 py-16 text-center">
        <div className="max-w-3xl mx-auto bg-gradient-to-r from-yellow-400 to-orange-400 rounded-3xl p-12">
          <h2 className="text-3xl md:text-4xl font-bold text-purple-900 mb-4">
            Ready to Go Viral?
          </h2>
          <p className="text-purple-900/80 text-lg mb-8">
            Join thousands of meme creators. Start making viral content today!
          </p>
          <Link to="/meme-maker">
            <Button size="lg" className="bg-purple-900 text-white hover:bg-purple-800 font-bold text-lg px-8 py-6">
              <Sparkles className="mr-2 h-5 w-5" />
              Create Your First Meme
            </Button>
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-white/10 bg-black/20 backdrop-blur-sm py-8">
        <div className="container mx-auto px-4 text-center text-gray-400">
          <p>© 2026 MimeMaker.com - Create viral memes instantly</p>
        </div>
      </footer>
    </div>
  );
}